import React, { FunctionComponent, useState, ChangeEvent } from 'react';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { Checkbox, TextField, FormControlLabel, makeStyles, Typography } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { DropDownField } from 'src/store/modules/application/types';

type AutoCompleteSelectProps = {
    value?: any;
    id?: string;
    label?: string;
    error?: boolean;
    classes?: object;
    ariaLabel?: string;
    onChange: Function;
    data: DropDownField[];
    isMultipleValue?: boolean;
    disableClearable?: boolean;
    textFieldInputProps?: object;
    disableCloseOnSelect?: boolean;
    size?: 'small' | 'medium';
    inputSize?: 'small' | 'medium';
    variant?: 'outlined' | 'standard';
    disabled?: boolean;
    placeholder?: string;
    instruction?: string;
    onTextInputValueChange?: Function;
};
const useStyles = makeStyles({
    root: {
        '&>.MuiListItem-gutters': {
            paddingLeft: 0,
            paddingRight: 0,
        },
        padding: 0,
        width: '100%',
    },
    divider: {
        borderBottom: '1px dashed #484848',
    },
    label: {
        marginLeft: 8,
        fontWeight: 600,
        color: '#484848',
    },
    disabledInstruction: {
        fontSize: 14,
        color: '#484848',
    },
});
const AutoCompleteSelect: FunctionComponent<AutoCompleteSelectProps> = (props) => {
    const {
        id,
        data,
        size,
        error,
        label,
        value,
        variant,
        classes,
        onChange,
        inputSize,
        ariaLabel,
        isMultipleValue,
        disableClearable,
        textFieldInputProps,
        disableCloseOnSelect,
        disabled,
        placeholder,
        instruction,
        onTextInputValueChange,
    } = props;
    const localStyles = useStyles();
    const filter = createFilterOptions();
    const [availableOptions, setAvailableOptions] = useState<DropDownField[]>([]);
    const [inputText, setInputText] = useState<string>('');
    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon color="primary" fontSize="small" />;
    const allSelected = data && data.length === (value ? value.length : 0);

    const handleChange = (e: React.ChangeEvent<object>, dropDownData: DropDownField | DropDownField[] | null) => {
        if (
            Array.isArray(dropDownData) &&
            dropDownData?.find((option: DropDownField) => option.value === 'select-all')
        ) {
            setInputText('');
            onChange(!allSelected ? (inputText ? availableOptions.concat(dropDownData) : data) : []);
        } else {
            onChange(dropDownData);
        }
    };

    const filterOptions = (data: DropDownField[], params: any) => {
        const filteredOptions = filter(data, params);
        return isMultipleValue
            ? filteredOptions.length
                ? ([{ label: 'Select All', value: 'select-all' }, ...filteredOptions] as DropDownField[])
                : (filteredOptions as DropDownField[])
            : ([{ label: instruction, value: instruction }, ...filteredOptions] as DropDownField[]);
    };

    const getOptionLabel = (data: DropDownField) => (data.label ? data.label : '');

    const handleInputTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInputText(event.target.value);
        const filtered = filter(data, {
            inputValue: event.target.value,
            getOptionLabel: (data: DropDownField) => {
                return data.label;
            },
        } as any);
        setAvailableOptions(filtered as DropDownField[]);
    };

    const setInputTextValue = (value: string) => {
        if (onTextInputValueChange) {
            onTextInputValueChange(value);
        }
    };

    return (
        <React.Fragment>
            <Autocomplete
                id={id}
                value={value}
                classes={classes}
                onChange={handleChange}
                multiple={isMultipleValue}
                size={!!size ? size : 'medium'}
                disableClearable={disableClearable}
                options={data}
                filterOptions={filterOptions}
                disableCloseOnSelect={disableCloseOnSelect}
                getOptionSelected={(data: DropDownField, value: DropDownField) => value?.value === data.value}
                getOptionDisabled={(value: DropDownField) => value?.value === instruction}
                disabled={disabled}
                getOptionLabel={getOptionLabel}
                onInputChange={(event: any) => setInputTextValue(event?.target?.value)}
                renderInput={(params) =>
                    variant === 'standard' ? (
                        <TextField
                            {...params}
                            label={label}
                            error={error}
                            size={inputSize}
                            variant="standard"
                            aria-label={ariaLabel}
                            classes={textFieldInputProps}
                            onChange={handleInputTextChange}
                            placeholder={placeholder}
                        />
                    ) : (
                        variant === 'outlined' && (
                            <TextField
                                {...params}
                                label={label}
                                error={error}
                                size={inputSize}
                                variant="outlined"
                                aria-label={ariaLabel}
                                classes={textFieldInputProps}
                                onChange={handleInputTextChange}
                                placeholder={placeholder}
                            />
                        )
                    )
                }
                renderOption={(data, { selected }) => {
                    const isOptionSelectAll = data.value === 'select-all';
                    const selectAllProps = isOptionSelectAll ? { checked: allSelected } : {};
                    return (
                        <>
                            {isOptionSelectAll ? (
                                <div style={{ width: '100%' }}>
                                    <FormControlLabel
                                        classes={{
                                            label: localStyles.label,
                                        }}
                                        control={
                                            <Checkbox
                                                icon={icon}
                                                checked={allSelected}
                                                checkedIcon={checkedIcon}
                                                {...selectAllProps}
                                                style={{ marginLeft: 10 }}
                                            />
                                        }
                                        label={data.label}
                                    />
                                    <div className={localStyles.divider} />
                                </div>
                            ) : isMultipleValue ? (
                                <>
                                    <Checkbox
                                        icon={icon}
                                        checked={selected}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                    />
                                    {data.label}
                                </>
                            ) : (
                                <React.Fragment>
                                    {data.value !== instruction ? (
                                        <Typography>{data.label}</Typography>
                                    ) : (
                                        <Typography className={localStyles.disabledInstruction}>
                                            {instruction}
                                        </Typography>
                                    )}
                                </React.Fragment>
                            )}
                        </>
                    );
                }}
            />
        </React.Fragment>
    );
};

export default AutoCompleteSelect;
